

import React, { useRef, useState, useEffect } from "react";
import Slider from "react-slick";
import { FaTrashAlt } from "react-icons/fa";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

const GalleryCards = () => {
    const [images, setImages] = useState([]);
    const [aboutUsId, setAboutUsId] = useState(null);
    const [vendorId, setVendorId] = useState(null);
    const fileInputRef = useRef(null);

    // Get vendorId on mount
    useEffect(() => {
        const storedVendorId = localStorage.getItem("vendorId");
        setVendorId(storedVendorId);
    }, []);

    // Fetch existing About Us images
    useEffect(() => {
        if (vendorId) fetchAboutUsData(vendorId);
    }, [vendorId]);

    const fetchAboutUsData = async (vId) => {
        const token = localStorage.getItem("authToken");
        if (!token) return alert("Authorization token missing.");

        try {
            const response = await fetch(
                `https://backend.maamaas.com/food/api/vendor/aboutus/get/${vId}`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            if (!response.ok) return;

            const data = await response.json();
            const galleryData = Array.isArray(data) ? data[0] : data;
            setAboutUsId(galleryData.aboutUsId || null);

            // Map backend image URLs
            const backendImages = [];
            ["image", "image1", "image2", "image3", "image4"].forEach((field) => {
                if (galleryData[field]) {
                    backendImages.push({
                        id: field,
                        src: galleryData[field],
                        isNew: false,
                    });
                }
            });

            setImages(backendImages); // ✅ Show gallery from backend URLs
        } catch (err) {
            console.error("Error fetching About Us:", err);
        }
    };


    const settings = {
        dots: false,
        infinite: images.length > 2,
        speed: 500,
        slidesToShow: Math.min(images.length, 3),
        slidesToScroll: 1,
        autoplay: images.length > 0,
        autoplaySpeed: 2000,
        pauseOnHover: true,
        responsive: [
            { breakpoint: 1024, settings: { slidesToShow: Math.min(images.length, 2), infinite: images.length > 1 } },
            { breakpoint: 768, settings: { slidesToShow: Math.min(images.length, 1), infinite: images.length > 0 } },
        ],
    };

    const handleAddImageClick = () => {
        if (!vendorId) return alert("User not identified. Please log in.");
        if (images.length >= 4) return alert("Maximum 4 images allowed.");
        fileInputRef.current.click();
    };

    const handleFileChange = async (event) => {
        if (!aboutUsId || !vendorId) return;
        const files = Array.from(event.target.files);
        const newImages = files.map(file => ({ file, isNew: true, src: URL.createObjectURL(file) }));
        const updatedImages = [...images, ...newImages];
        if (updatedImages.length > 4) {
            alert("Maximum 4 images allowed.");
            return;
        }
        setImages(updatedImages);

        // Send images as multipart/form-data
        await sendImagesToAPI(updatedImages);
    };

    const sendImagesToAPI = async (currentImagesArray) => {
        const token = localStorage.getItem("authToken");
        if (!token) return alert("Authorization token missing.");
        if (!aboutUsId) return alert("About Us ID missing.");

        try {
            const formData = new FormData();

            // Add the aboutUsData as JSON blob
            const aboutUsBlob = new Blob([JSON.stringify({ aboutUsId })], { type: "application/json" });
            formData.append("aboutUsData", aboutUsBlob);

            // Append images
            currentImagesArray.forEach((img, index) => {
                if (img.file) formData.append(`image${index + 1}`, img.file);
            });

            const response = await fetch(
                `https://backend.maamaas.com/food/api/vendor/aboutus/update/${aboutUsId}`,
                {
                    method: "PUT",
                    headers: { Authorization: `Bearer ${token}` },
                    body: formData,
                }
            );

            // console.log("HTTP status:", response.status);
            const responseText = await response.text();
            // console.log("Response body:", responseText);

            if (!response.ok) {
                alert(`Update failed with status ${response.status}. Check console for details.`);
                return;
            }

            // Refresh gallery after successful upload
            fetchAboutUsData(vendorId);
            alert("Images updated successfully!");
        } catch (err) {
            console.error("Error while updating:", err);
            alert("Error while updating. Check console for details.");
        }
    };

    const handleDelete = (index) => {
        const updatedImages = images.filter((_, i) => i !== index);
        setImages(updatedImages);
        if (aboutUsId && vendorId) sendImagesToAPI(updatedImages);
    };

    const ImageCard = ({ src, index }) => (
        <div className="image-card-container">
            <img src={src} alt={`gallery-item-${index}`} className="gallery-image" />
            <button onClick={(e) => { e.stopPropagation(); handleDelete(index); }} className="delete-button">
                <FaTrashAlt size={14} color="red" />
            </button>
        </div>
    );

    return (
        <section className="gallery-section">
            <div className="gallery-header">
                <h1 className="gallery-title">
                    <span className="title-red">Gall</span>
                    <span className="title-black">ery</span>
                </h1>

                <button onClick={handleAddImageClick} disabled={images.length >= 4 || !vendorId} className="add-button">
                    Add+
                </button>
                <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange}
                    className="file-input"
                    multiple
                />
            </div>

            {images.length > 0 ? (
                <Slider key={images.length} {...settings}>
                    {images.map((img, idx) => (
                        <div key={idx} className="slider-item">
                            <ImageCard src={img.src} index={idx} />
                        </div>
                    ))}
                </Slider>
            ) : (
                <p className="empty-message">No gallery images uploaded yet. Click "Add+" to upload.</p>
            )}

            <style jsx>{`
                .gallery-section { width: 100%; max-width: 1200px; margin: 0 auto; padding: 20px 15px; }
                .gallery-header { display: flex; align-items: center; margin-bottom: 20px; justify-content: center; flex-wrap: wrap; gap: 15px; }
                .gallery-title { font-size: 38px; font-weight: bold; letter-spacing: 1px; text-transform: uppercase; text-shadow: 0 4px 6px rgba(0,0,0,0.1); margin: 0; text-align: center; }
                .title-red { color: red; }
                .title-black { color: black; }
                .add-button { height: 35px; border-radius: 5px; border: none; background: #b15cd6; color: #fff; font-weight: bold; cursor: pointer; width: 80px; transition: all 0.3s ease; }
                .add-button:disabled { background: #ccc; cursor: not-allowed; }
                .file-input { display: none; }
                .image-card-container { position: relative; width: 100%; height: 300px; overflow: hidden; border-radius: 16px; margin: 0 10px; }
                .gallery-image { width: 100%; height: 100%; object-fit: cover; }
                .delete-button { position: absolute; top: 10px; right: 10px; background: rgba(0,0,0,0.6); border: none; border-radius: 50%; padding: 6px; color: white; cursor: pointer; display: flex; align-items: center; justify-content: center; z-index: 2; }
                .empty-message { text-align: center; color: #777; font-style: italic; padding: 20px; }
            `}</style>
        </section>
    );
};

export default GalleryCards;




// import React, { useRef, useState, useEffect } from "react";
// import Slider from "react-slick";
// import { FaTrashAlt, FaPlus, FaSpinner, FaImage } from "react-icons/fa";
// import "slick-carousel/slick/slick.css";
// import "slick-carousel/slick/slick-theme.css";

// const GalleryCards = () => {
//     const [images, setImages] = useState([]);
//     const [aboutUsId, setAboutUsId] = useState(null);
//     const [vendorId, setVendorId] = useState(null);
//     const [loading, setLoading] = useState(true);
//     const [uploading, setUploading] = useState(false);
//     const fileInputRef = useRef(null);

//     // Get vendorId on mount
//     useEffect(() => {
//         const storedVendorId = localStorage.getItem("vendorId");
//         setVendorId(storedVendorId);
//     }, []);

//     // Fetch existing About Us images
//     useEffect(() => {
//         if (vendorId) fetchAboutUsData(vendorId);
//     }, [vendorId]);

//     const fetchAboutUsData = async (vId) => {
//         const token = localStorage.getItem("authToken");
//         if (!token) return alert("Authorization token missing.");

//         setLoading(true);
//         try {
//             const response = await fetch(
//                 `https://backend.maamaas.com/food/api/vendor/aboutus/get/${vId}`,
//                 { headers: { Authorization: `Bearer ${token}` } }
//             );
//             if (!response.ok) return;

//             const data = await response.json();
//             const galleryData = Array.isArray(data) ? data[0] : data;
//             setAboutUsId(galleryData.aboutUsId || null);

//             // Map backend image URLs
//             const backendImages = [];
//             ["image", "image1", "image2", "image3", "image4"].forEach((field) => {
//                 if (galleryData[field]) {
//                     backendImages.push({
//                         id: field,
//                         src: galleryData[field],
//                         isNew: false,
//                     });
//                 }
//             });

//             setImages(backendImages);
//         } catch (err) {
//             console.error("Error fetching About Us:", err);
//         } finally {
//             setLoading(false);
//         }
//     };

//     const settings = {
//         dots: true,
//         infinite: images.length > 3,
//         speed: 500,
//         slidesToShow: Math.min(images.length, 4),
//         slidesToScroll: 1,
//         autoplay: images.length > 0,
//         autoplaySpeed: 3000,
//         pauseOnHover: true,
//         arrows: true,
//         cssEase: "cubic-bezier(0.645, 0.045, 0.355, 1)",
//         responsive: [
//             {
//                 breakpoint: 1200,
//                 settings: {
//                     slidesToShow: Math.min(images.length, 3),
//                     infinite: images.length > 2
//                 }
//             },
//             {
//                 breakpoint: 992,
//                 settings: {
//                     slidesToShow: Math.min(images.length, 2),
//                     infinite: images.length > 1
//                 }
//             },
//             {
//                 breakpoint: 768,
//                 settings: {
//                     slidesToShow: Math.min(images.length, 1),
//                     infinite: images.length > 0
//                 }
//             }
//         ]
//     };

//     const handleAddImageClick = () => {
//         if (!vendorId) {
//             alert("Please log in to upload images.");
//             return;
//         }
//         if (images.length >= 4) {
//             alert("Maximum 4 images allowed.");
//             return;
//         }
//         fileInputRef.current.click();
//     };

//     const handleFileChange = async (event) => {
//         if (!aboutUsId || !vendorId) return;
//         const files = Array.from(event.target.files);
//         const newImages = files.map(file => ({
//             file,
//             isNew: true,
//             src: URL.createObjectURL(file),
//             name: file.name,
//             size: file.size
//         }));
//         const updatedImages = [...images, ...newImages];
        
//         if (updatedImages.length > 4) {
//             alert("Maximum 4 images allowed. Please select fewer files.");
//             return;
//         }
        
//         setImages(updatedImages);
//         setUploading(true);
//         await sendImagesToAPI(updatedImages);
//         setUploading(false);
//     };

//     const sendImagesToAPI = async (currentImagesArray) => {
//         const token = localStorage.getItem("authToken");
//         if (!token) return alert("Authorization token missing.");
//         if (!aboutUsId) return alert("About Us ID missing.");

//         try {
//             const formData = new FormData();
//             const aboutUsBlob = new Blob([JSON.stringify({ aboutUsId })], { type: "application/json" });
//             formData.append("aboutUsData", aboutUsBlob);

//             currentImagesArray.forEach((img, index) => {
//                 if (img.file) formData.append(`image${index + 1}`, img.file);
//             });

//             const response = await fetch(
//                 `https://backend.maamaas.com/food/api/vendor/aboutus/update/${aboutUsId}`,
//                 {
//                     method: "PUT",
//                     headers: { Authorization: `Bearer ${token}` },
//                     body: formData,
//                 }
//             );

//             if (!response.ok) {
//                 alert(`Update failed with status ${response.status}.`);
//                 return;
//             }

//             fetchAboutUsData(vendorId);
//         } catch (err) {
//             console.error("Error while updating:", err);
//             alert("Error while updating images.");
//         }
//     };

//     const handleDelete = async (index) => {
//         if (!window.confirm("Are you sure you want to delete this image?")) return;
        
//         const updatedImages = images.filter((_, i) => i !== index);
//         setImages(updatedImages);
//         if (aboutUsId && vendorId) {
//             setUploading(true);
//             await sendImagesToAPI(updatedImages);
//             setUploading(false);
//         }
//     };

//     const ImageCard = ({ src, index, name, size }) => (
//         <div className="image-card-container">
//             <div className="image-wrapper">
//                 <img 
//                     src={src} 
//                     alt={`gallery-item-${index}`} 
//                     className="gallery-image" 
//                     loading="lazy"
//                     onError={(e) => {
//                         e.target.src = "https://via.placeholder.com/300x200/cccccc/969696?text=Image+Not+Found";
//                     }}
//                 />
//                 <div className="image-overlay">
//                     <span className="image-number">#{index + 1}</span>
//                     <button 
//                         onClick={(e) => { 
//                             e.stopPropagation(); 
//                             handleDelete(index); 
//                         }} 
//                         className="delete-button"
//                     >
//                         <FaTrashAlt size={16} />
//                     </button>
//                 </div>
//                 {name && (
//                     <div className="image-info">
//                         <span className="image-name">{name}</span>
//                         <span className="image-size">
//                             {(size / (1024 * 1024)).toFixed(2)} MB
//                         </span>
//                     </div>
//                 )}
//             </div>
//         </div>
//     );

//     return (
//         <section className="gallery-section">
//             <div className="gallery-header">
//                 <div className="title-section">
//                     <h1 className="gallery-title">
//                         <span className="title-red">Gallery</span>
//                         <span className="title-sub">Showcase Your Best Moments</span>
//                     </h1>
//                     <p className="gallery-subtitle">
//                         Upload up to 4 high-quality images to display in your gallery
//                     </p>
//                 </div>

//                 <div className="action-section">
//                     <div className="image-count">
//                         <FaImage className="count-icon" />
//                         <span className="count-text">{images.length}/4 images</span>
//                     </div>
//                     <button 
//                         onClick={handleAddImageClick} 
//                         disabled={images.length >= 4 || !vendorId || uploading} 
//                         className="add-button"
//                     >
//                         {uploading ? (
//                             <FaSpinner className="spin" />
//                         ) : (
//                             <>
//                                 <FaPlus className="plus-icon" />
//                                 Add Image
//                             </>
//                         )}
//                     </button>
//                     <input
//                         ref={fileInputRef}
//                         type="file"
//                         accept="image/*"
//                         onChange={handleFileChange}
//                         className="file-input"
//                         multiple
//                     />
//                 </div>
//             </div>

//             {loading ? (
//                 <div className="loading-container">
//                     <FaSpinner className="loading-spinner" />
//                     <p>Loading gallery...</p>
//                 </div>
//             ) : images.length > 0 ? (
//                 <div className="gallery-slider-container">
//                     <Slider {...settings}>
//                         {images.map((img, idx) => (
//                             <div key={idx} className="slider-item">
//                                 <ImageCard 
//                                     src={img.src} 
//                                     index={idx} 
//                                     name={img.name}
//                                     size={img.size}
//                                 />
//                             </div>
//                         ))}
//                     </Slider>
//                 </div>
//             ) : (
//                 <div className="empty-state">
//                     <div className="empty-icon">
//                         <FaImage size={64} />
//                     </div>
//                     <h3>No Images Yet</h3>
//                     <p>Start building your gallery by uploading some photos</p>
//                     <button 
//                         onClick={handleAddImageClick} 
//                         disabled={!vendorId} 
//                         className="upload-first-button"
//                     >
//                         <FaPlus /> Upload Your First Image
//                     </button>
//                 </div>
//             )}

//             <style jsx>{`
//                 .gallery-section {
//                     width: 100%;
//                     max-width: 1400px;
//                     margin: 0 auto;
//                     padding: 40px 20px;
//                     background: linear-gradient(135deg, #f8f9ff 0%, #ffffff 100%);
//                     border-radius: 24px;
//                     box-shadow: 0 10px 40px rgba(0, 0, 0, 0.08);
//                     position: relative;
//                     overflow: hidden;
//                 }

//                 .gallery-section::before {
//                     content: '';
//                     position: absolute;
//                     top: 0;
//                     left: 0;
//                     right: 0;
//                     height: 4px;
//                     background: linear-gradient(90deg, #ff6b6b 0%, #4ecdc4 100%);
//                 }

//                 .gallery-header {
//                     display: flex;
//                     justify-content: space-between;
//                     align-items: flex-start;
//                     margin-bottom: 40px;
//                     flex-wrap: wrap;
//                     gap: 20px;
//                 }

//                 .title-section {
//                     flex: 1;
//                     min-width: 300px;
//                 }

//                 .gallery-title {
//                     font-size: 42px;
//                     font-weight: 800;
//                     letter-spacing: -0.5px;
//                     margin: 0 0 12px 0;
//                     background: linear-gradient(90deg, #ff6b6b, #4ecdc4);
//                     -webkit-background-clip: text;
//                     -webkit-text-fill-color: transparent;
//                     background-clip: text;
//                 }

//                 .title-sub {
//                     display: block;
//                     font-size: 16px;
//                     font-weight: 400;
//                     color: #666;
//                     margin-top: 8px;
//                 }

//                 .gallery-subtitle {
//                     color: #666;
//                     font-size: 16px;
//                     line-height: 1.6;
//                     margin: 0;
//                     max-width: 500px;
//                 }

//                 .action-section {
//                     display: flex;
//                     align-items: center;
//                     gap: 20px;
//                     flex-wrap: wrap;
//                 }

//                 .image-count {
//                     display: flex;
//                     align-items: center;
//                     gap: 8px;
//                     padding: 10px 20px;
//                     background: #f0f7ff;
//                     border-radius: 50px;
//                     font-weight: 600;
//                     color: #2c3e50;
//                 }

//                 .count-icon {
//                     color: #4ecdc4;
//                 }

//                 .add-button {
//                     display: flex;
//                     align-items: center;
//                     gap: 10px;
//                     padding: 12px 28px;
//                     background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
//                     color: white;
//                     border: none;
//                     border-radius: 50px;
//                     font-weight: 600;
//                     font-size: 16px;
//                     cursor: pointer;
//                     transition: all 0.3s ease;
//                     box-shadow: 0 4px 15px rgba(102, 126, 234, 0.3);
//                 }

//                 .add-button:hover:not(:disabled) {
//                     transform: translateY(-2px);
//                     box-shadow: 0 6px 20px rgba(102, 126, 234, 0.4);
//                 }

//                 .add-button:disabled {
//                     opacity: 0.5;
//                     cursor: not-allowed;
//                     transform: none;
//                 }

//                 .plus-icon {
//                     font-size: 14px;
//                 }

//                 .file-input {
//                     display: none;
//                 }

//                 .gallery-slider-container {
//                     padding: 20px 10px;
//                 }

//                 .slider-item {
//                     padding: 15px;
//                     outline: none;
//                 }

//                 .image-card-container {
//                     position: relative;
//                     width: 100%;
//                     height: 320px;
//                     border-radius: 20px;
//                     overflow: hidden;
//                     transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
//                     box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
//                 }

//                 .image-card-container:hover {
//                     transform: translateY(-8px);
//                     box-shadow: 0 20px 40px rgba(0, 0, 0, 0.2);
//                 }

//                 .image-wrapper {
//                     position: relative;
//                     width: 100%;
//                     height: 100%;
//                 }

//                 .gallery-image {
//                     width: 100%;
//                     height: 100%;
//                     object-fit: cover;
//                     transition: transform 0.5s ease;
//                 }

//                 .image-card-container:hover .gallery-image {
//                     transform: scale(1.05);
//                 }

//                 .image-overlay {
//                     position: absolute;
//                     top: 0;
//                     left: 0;
//                     right: 0;
//                     padding: 20px;
//                     background: linear-gradient(to bottom, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0) 100%);
//                     display: flex;
//                     justify-content: space-between;
//                     align-items: flex-start;
//                     opacity: 0;
//                     transition: opacity 0.3s ease;
//                 }

//                 .image-card-container:hover .image-overlay {
//                     opacity: 1;
//                 }

//                 .image-number {
//                     color: white;
//                     font-weight: bold;
//                     background: rgba(255, 255, 255, 0.2);
//                     padding: 4px 12px;
//                     border-radius: 20px;
//                     font-size: 14px;
//                     backdrop-filter: blur(10px);
//                 }

//                 .delete-button {
//                     background: rgba(255, 255, 255, 0.2);
//                     border: none;
//                     width: 36px;
//                     height: 36px;
//                     border-radius: 50%;
//                     color: white;
//                     cursor: pointer;
//                     display: flex;
//                     align-items: center;
//                     justify-content: center;
//                     transition: all 0.3s ease;
//                     backdrop-filter: blur(10px);
//                 }

//                 .delete-button:hover {
//                     background: rgba(231, 76, 60, 0.8);
//                     transform: scale(1.1);
//                 }

//                 .image-info {
//                     position: absolute;
//                     bottom: 0;
//                     left: 0;
//                     right: 0;
//                     padding: 15px;
//                     background: linear-gradient(to top, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0) 100%);
//                     color: white;
//                     transform: translateY(100%);
//                     transition: transform 0.3s ease;
//                 }

//                 .image-card-container:hover .image-info {
//                     transform: translateY(0);
//                 }

//                 .image-name {
//                     display: block;
//                     font-size: 12px;
//                     white-space: nowrap;
//                     overflow: hidden;
//                     text-overflow: ellipsis;
//                 }

//                 .image-size {
//                     font-size: 11px;
//                     opacity: 0.8;
//                 }

//                 .loading-container {
//                     display: flex;
//                     flex-direction: column;
//                     align-items: center;
//                     justify-content: center;
//                     padding: 60px 20px;
//                 }

//                 .loading-spinner {
//                     font-size: 48px;
//                     color: #667eea;
//                     animation: spin 1s linear infinite;
//                     margin-bottom: 20px;
//                 }

//                 .empty-state {
//                     text-align: center;
//                     padding: 60px 20px;
//                     background: linear-gradient(135deg, #f8f9ff 0%, #eef1ff 100%);
//                     border-radius: 20px;
//                     border: 2px dashed #c5d0ff;
//                 }

//                 .empty-icon {
//                     color: #a0aec0;
//                     margin-bottom: 20px;
//                 }

//                 .empty-state h3 {
//                     color: #2d3748;
//                     margin-bottom: 10px;
//                     font-size: 24px;
//                 }

//                 .empty-state p {
//                     color: #718096;
//                     margin-bottom: 30px;
//                     max-width: 400px;
//                     margin-left: auto;
//                     margin-right: auto;
//                 }

//                 .upload-first-button {
//                     display: inline-flex;
//                     align-items: center;
//                     gap: 10px;
//                     padding: 14px 32px;
//                     background: linear-gradient(135deg, #4ecdc4 0%, #44a08d 100%);
//                     color: white;
//                     border: none;
//                     border-radius: 50px;
//                     font-weight: 600;
//                     font-size: 16px;
//                     cursor: pointer;
//                     transition: all 0.3s ease;
//                 }

//                 .upload-first-button:hover:not(:disabled) {
//                     transform: translateY(-2px);
//                     box-shadow: 0 10px 25px rgba(78, 205, 196, 0.4);
//                 }

//                 .upload-first-button:disabled {
//                     opacity: 0.5;
//                     cursor: not-allowed;
//                 }

//                 .spin {
//                     animation: spin 1s linear infinite;
//                 }

//                 @keyframes spin {
//                     0% { transform: rotate(0deg); }
//                     100% { transform: rotate(360deg); }
//                 }

//                 /* Custom slider styles */
//                 :global(.slick-prev:before, .slick-next:before) {
//                     color: #667eea !important;
//                     font-size: 24px !important;
//                 }

//                 :global(.slick-dots li button:before) {
//                     color: #667eea !important;
//                     font-size: 10px !important;
//                 }

//                 :global(.slick-dots li.slick-active button:before) {
//                     color: #ff6b6b !important;
//                 }

//                 :global(.slick-slide) {
//                     padding: 0 10px;
//                 }

//                 @media (max-width: 768px) {
//                     .gallery-section {
//                         padding: 30px 15px;
//                         border-radius: 16px;
//                     }

//                     .gallery-title {
//                         font-size: 32px;
//                     }

//                     .gallery-header {
//                         flex-direction: column;
//                         text-align: center;
//                     }

//                     .title-section {
//                         text-align: center;
//                     }

//                     .gallery-subtitle {
//                         margin: 0 auto;
//                     }

//                     .action-section {
//                         justify-content: center;
//                     }

//                     .image-card-container {
//                         height: 280px;
//                     }
//                 }

//                 @media (max-width: 480px) {
//                     .gallery-title {
//                         font-size: 28px;
//                     }

//                     .add-button, .upload-first-button {
//                         width: 100%;
//                         justify-content: center;
//                     }

//                     .image-count {
//                         width: 100%;
//                         justify-content: center;
//                     }

//                     .image-card-container {
//                         height: 240px;
//                     }
//                 }
//             `}</style>
//         </section>
//     );
// };

// export default GalleryCards;